import classnames from 'classnames';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';
import { func, number, objectOf, shape, string } from 'prop-types';
import React, { useEffect } from 'react';

import { getSpentBudgetForProduct } from '../../campaign-editor/utils';
import CurrencyFormat from '../../generic/CurrencyFormat';
import { campaignShape } from '../../prop-types';
import { WRITABLE_BUDGET_FIELDS } from '../change-product-budget/constants';
import { calculateTotalDelta } from '../transfer-budget/utils';

const hasAnyPositiveDeltaForField = (productAggregates, field) =>
  productAggregates.some(
    agg => agg.newValues[field] - agg.oldValues[field] > 0,
  );

const getTotalSpentBudget = (campaign, productAggregates) =>
  sum(
    productAggregates.map(({ product }) =>
      getSpentBudgetForProduct(campaign, product),
    ),
  );

export default function MoveBudgetToWalletTotals({
  productAggregatesMap,
  totalNewBudget,
  totalOldBudget,
  campaign,
  onChange,
}) {
  const productAggregates = Object.values(productAggregatesMap);
  const totalSpentBudget = getTotalSpentBudget(campaign, productAggregates);
  const totalLeftBudget = totalNewBudget - totalSpentBudget;
  const totalDelta = totalNewBudget - totalOldBudget;

  useEffect(() => {
    const roundedDelta = Math.round(totalDelta * 100) / 100;
    onChange({ target: { name: 'delta', value: roundedDelta } });
  }, [onChange, totalDelta]);

  return (
    <tr className="bg-gray-200">
      <td>Totals</td>
      <td>&nbsp;</td>
      {WRITABLE_BUDGET_FIELDS.map(field => {
        const delta = calculateTotalDelta(productAggregates, field);
        const hasPositiveDelta = hasAnyPositiveDeltaForField(
          productAggregates,
          field,
        );

        return [
          <td key={`old-${field}`} className="text-right">
            <CurrencyFormat
              name={`total-old-${field}`}
              value={sumBy(productAggregates, `oldValues.${field}`)}
              className="text-nowrap"
              text
            />
          </td>,
          <td key={`new-${field}`} className="text-right">
            <CurrencyFormat
              name={`total-new-${field}`}
              value={sumBy(productAggregates, `newValues.${field}`)}
              className="text-nowrap"
              text
            />
          </td>,
          <td
            key={`delta-${field}`}
            className={classnames('text-right', {
              'cr-bg-red': delta > 0 || hasPositiveDelta,
              'cr-bg-green': delta < 0 && !hasPositiveDelta,
            })}
            style={
              delta === 0 && !hasPositiveDelta
                ? { backgroundColor: 'transparent' }
                : {}
            }
          >
            <CurrencyFormat
              name={`total-delta-${field}`}
              value={delta}
              className={classnames('text-nowrap text-info', {
                'cr-text-white': delta !== 0,
              })}
              text
            />
          </td>,
        ];
      })}
      <td className="text-right">
        <CurrencyFormat
          name="total-old-budget"
          value={totalOldBudget}
          className="text-nowrap"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-new-budget"
          value={totalNewBudget}
          className="text-nowrap"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-spent-budget"
          value={totalSpentBudget}
          className={classnames('text-nowrap text-danger', {
            'bg-danger text-white': totalNewBudget < totalSpentBudget,
          })}
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-left-budget"
          value={totalLeftBudget}
          className="text-nowrap text-success"
          text
        />
      </td>
      <td
        className="text-right"
        style={
          totalDelta === 0 &&
          !WRITABLE_BUDGET_FIELDS.some(field =>
            hasAnyPositiveDeltaForField(productAggregates, field),
          )
            ? { backgroundColor: 'transparent' }
            : {}
        }
      >
        <CurrencyFormat
          name="total-delta"
          value={totalDelta}
          className={classnames('text-nowrap text-info', {
            'cr-bg-red':
              totalDelta > 0 ||
              WRITABLE_BUDGET_FIELDS.some(field =>
                hasAnyPositiveDeltaForField(productAggregates, field),
              ),
            'cr-bg-green':
              totalDelta < 0 &&
              !WRITABLE_BUDGET_FIELDS.some(field =>
                hasAnyPositiveDeltaForField(productAggregates, field),
              ),
            'cr-text-white': totalDelta !== 0,
          })}
          text
        />
      </td>
    </tr>
  );
}

const budgetFieldsShape = shape({
  netClientBudgetExternal: number.isRequired,
  wkzBudget: number.isRequired,
  coInvest: number.isRequired,
  freeService: number.isRequired,
});

MoveBudgetToWalletTotals.propTypes = {
  productAggregatesMap: objectOf(
    shape({
      label: string.isRequired,
      adjustedFee: number.isRequired,
      oldValues: budgetFieldsShape.isRequired,
      newValues: budgetFieldsShape.isRequired,
    }).isRequired,
  ).isRequired,
  totalNewBudget: number.isRequired,
  totalOldBudget: number.isRequired,
  campaign: campaignShape.isRequired,
  onChange: func.isRequired,
};
